import React, { useMemo, useState } from 'react';
import { Badge, Col, Form, ProgressBar, Row, Table } from 'react-bootstrap';

import ExportCSVButton from '../shared-components/ExportButton';

// The codes table — READ-ONLY (Gwen 2026-08-24): no enable/disable surface
// at all. The wave's countdown makes codes inert at the cutoff and the
// close flips them off for good; Admin never manages code state by hand.
// Max uses is NOT editable after creation — the header liner says so where
// the number lives, so nobody hunts for an edit affordance. Entries already
// admitted always stay — that is the entries panel's business.
const CLASS_BADGE = { creator: 'warning', vvip: 'info', public: 'secondary', internal: 'dark' };

const CodesPanel = ({ codes, title = 'Codes' }) => {
  const [search, setSearch] = useState('');
  const [classFilter, setClassFilter] = useState('');
  const [batchFilter, setBatchFilter] = useState('');

  const batches = useMemo(
    () => [...new Set(codes.map(c => c.batchId).filter(Boolean))].sort(),
    [codes]
  );

  const shown = useMemo(() => {
    const needle = search.trim().toUpperCase();
    return codes.filter(c => {
      if (classFilter && c.classLabel !== classFilter) return false;
      if (batchFilter && c.batchId !== batchFilter) return false;
      if (
        needle &&
        !c.code.includes(needle) &&
        !(c.ownerLabel ?? '').toUpperCase().includes(needle)
      )
        return false;
      return true;
    });
  }, [codes, search, classFilter, batchFilter]);

  return (
    <div className="waitlist-panel">
      <h5>{title}</h5>
      <div className="text-muted small mb-2">
        Codes admit while their wave is open — the cutoff turns them all off.
      </div>
      <Row className="g-2 mb-2">
        <Col md={5}>
          <Form.Control
            placeholder="search code / owner…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </Col>
        <Col md={2}>
          <Form.Select
            aria-label="class filter"
            value={classFilter}
            onChange={e => setClassFilter(e.target.value)}
          >
            <option value="">all classes</option>
            {Object.keys(CLASS_BADGE).map(c => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </Form.Select>
        </Col>
        <Col md={2}>
          <Form.Select
            aria-label="batch filter"
            value={batchFilter}
            onChange={e => setBatchFilter(e.target.value)}
          >
            <option value="">all batches</option>
            {batches.map(b => (
              <option key={b} value={b}>
                {b}
              </option>
            ))}
          </Form.Select>
        </Col>
        <Col md="auto">
          <ExportCSVButton
            data={shown.map(c => {
              const row = { ...c };
              delete row.enabled;
              return row;
            })}
            filename="waitlist-codes.csv"
          />
        </Col>
      </Row>
      <Table hover responsive size="sm">
        <thead>
          <tr>
            <th>Code</th>
            <th>
              Uses <span className="text-warning">(set at mint — not editable)</span>
            </th>
            <th>Attributed to</th>
            <th>Class</th>
            <th>Batch</th>
            <th>Wave</th>
          </tr>
        </thead>
        <tbody>
          {shown.map(c => (
            <tr key={c.code}>
              <td className="fw-semibold">{c.code}</td>
              <td>
                <div className="d-flex align-items-center gap-2">
                  <ProgressBar
                    now={c.maxUses ? Math.round((c.useCount / c.maxUses) * 100) : 0}
                    style={{ width: 110, height: 8 }}
                    aria-label={`${c.code} usage`}
                  />
                  <span className="small">
                    <b>{c.useCount}</b> <span className="text-muted">/ {c.maxUses}</span>
                  </span>
                </div>
              </td>
              <td>{c.ownerLabel}</td>
              <td>
                <Badge bg={CLASS_BADGE[c.classLabel] ?? 'secondary'}>{c.classLabel}</Badge>
              </td>
              <td className="text-muted">{c.batchId ?? '—'}</td>
              <td className="text-muted">{c.waveName ?? '—'}</td>
            </tr>
          ))}
          {shown.length === 0 && (
            <tr>
              <td colSpan={6} className="text-center text-muted py-4">
                No codes match.
              </td>
            </tr>
          )}
        </tbody>
      </Table>
    </div>
  );
};

export default CodesPanel;
